import { type LinkingOptions } from '@atlaskit/editor-common/types';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, xcss } from '@atlaskit/primitives';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { type ADFDocument } from '@canvas/adf';
import { currentUserState } from '@canvas/canvas-state/user';
import { smartLinkClient } from '@canvas/smart-link-client';
import { useStablePlugins } from '@canvas/smart-link-picker/use-stable-plugins';

import { ErrorBoundary } from '../error-boundary.tsx';
import { cardProvider } from '../providers/card-provider.ts';
import { emojiProvider } from '../providers/emoji-provider.ts';
import { getMentionProvider } from '../providers/mention-provider.ts';
import { getEditorValue } from '../util.ts';

import {
  type DatabasesEditorAPI,
  useDatabasesPresetWithApi,
} from './databases-preset.ts';

export interface DatabasesEditorProps {
  disabled?: boolean;
  shouldFocus?: boolean;
  placeholderText?: string;
  defaultValue: ADFDocument | undefined;
  onChange: (value: ADFDocument) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  popupsMountPoint?: HTMLElement;
  onEditorApi?: (editorApi: DatabasesEditorAPI) => void;
}

export type { DatabasesEditorAPI };

const editorContainerStyles = xcss({
  fontFamily: 'inherit',
  width: '100%',
  textAlign: 'left',
});

/**
 * The chromeless ComposableEditor applies `padding-bottom: var(--ds-space-100)`
 * to `.ProseMirror > :last-child`. This extra padding makes the edit view taller
 * than the read view. There is no prop to disable this, so we override it with
 * a scoped selector.
 */
const DATABASES_EDITOR_CSS = `
  [data-databases-editor] .ProseMirror > :last-child { padding-bottom: 0 !important; }
`;

/**
 * Style overrides for the link picker floating toolbar and typeahead (slash) menu.
 *
 * The link picker popup renders via ProseMirror's floating toolbar, either inside
 * [data-databases-editor] or in a portal mount point. These rules fix inherited styles
 * from the database table that break the Atlaskit link picker's appearance.
 *
 * The typeahead overrides constrain the slash menu to match Confluence page styling.
 *
 * NOTE: Some selectors use data-testid attributes (e.g. [data-testid="link-picker"],
 * [data-testid="link-search-list-item"]) because these are the only stable hooks
 * available on Atlaskit internals. These may need updating if the link-picker or
 * editor-plugins packages change their test IDs.
 */
const LINK_PICKER_CSS = `
  .hyperlink-floating-toolbar label {
    font-weight: 600 !important;
    font-size: 12px !important;
  }
  .hyperlink-floating-toolbar input {
    font: var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif) !important;
    color: var(--ds-text-subtlest, #626F86) !important;
  }
  .hyperlink-floating-toolbar input::placeholder {
    font: var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif) !important;
    color: var(--ds-text-subtlest, #626F86) !important;
  }
  .hyperlink-floating-toolbar [data-ds--text-field--container] {
    border: 2px solid var(--ds-border-input, #8590A2) !important;
    border-radius: 6px !important;
    box-shadow: none !important;
  }
  .hyperlink-floating-toolbar [data-ds--text-field--container]:focus-within {
    border-color: var(--ds-border-focused, #388BFF) !important;
    box-shadow: none !important;
  }
  .hyperlink-floating-toolbar,
  .hyperlink-floating-toolbar [data-testid="floating-toolbar-items"],
  .hyperlink-floating-toolbar [data-testid="editor-floating-toolbar-items"] {
    padding: 0 !important;
    margin: 0 !important;
  }
  .hyperlink-floating-toolbar [data-testid="link-picker"] > div:first-of-type {
    margin-bottom: 8px !important;
  }
  .hyperlink-floating-toolbar [data-ds--text-field--input] {
    padding-left: 8px !important;
  }
  .hyperlink-floating-toolbar [data-testid="link-search-list-item"]:nth-child(n+4) {
    display: none !important;
  }
  .hyperlink-floating-toolbar [data-testid="link-picker"] > div:last-of-type {
    --link-picker-min-height: auto !important;
    min-height: auto !important;
  }
  .fabric-editor-typeahead {
    width: 320px !important;
    max-width: 320px !important;
  }
  .fabric-editor-typeahead [role="listbox"] [role="option"] {
    max-height: 68px !important;
    min-height: 36px !important;
    padding: 8px 12px !important;
    box-sizing: border-box !important;
  }
  .fabric-editor-typeahead {
    --ds-space-500: 32px;
  }
  .fabric-editor-typeahead [role="option"] [data-item-elem-before] > div {
    border-color: var(--ds-border, #DFE1E5) !important;
  }
  .fabric-editor-typeahead [role="listbox"] [role="option"] {
    --ds-space-050: 0px;
  }
  .fabric-editor-typeahead [role="option"] [data-item-title] div > div + div {
    font: var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif) !important;
    margin-top: 0 !important;
  }
`;

const DatabasesEditorInternal: React.FC<DatabasesEditorProps> = ({
  disabled,
  shouldFocus,
  placeholderText,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  popupsMountPoint,
  onEditorApi,
}) => {
  const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null);
  const mountPoint = popupsMountPoint ?? portalElement ?? undefined;

  const accountId = useSnapshot(currentUserState)?.identity?.accountId;
  const { plugins: linkPickerPlugins } = useStablePlugins();

  const linkPickerOptions = useMemo(
    () => (linkPickerPlugins.length > 0 ? { plugins: linkPickerPlugins } : undefined),
    [linkPickerPlugins],
  );

  const linkPickerOptionsAtMountRef = useRef(linkPickerOptions);
  useEffect(() => {
    if (!shouldFocus) {
      linkPickerOptionsAtMountRef.current = linkPickerOptions;
    }
  }, [linkPickerOptions, shouldFocus]);

  const stableLinkPickerOptions = shouldFocus
    ? linkPickerOptionsAtMountRef.current
    : linkPickerOptions;

  const { preset, editorApi } = useDatabasesPresetWithApi({
    placeholderText,
    linkPicker: stableLinkPickerOptions,
  });

  const linkingOptions: LinkingOptions = useMemo(
    () => ({
      smartLinks: {
        provider: cardProvider,
      },
      linkPicker: stableLinkPickerOptions,
    }),
    [stableLinkPickerOptions],
  );

  useEffect(() => {
    onEditorApi?.(editorApi);
  }, [editorApi, onEditorApi]);

  // mountPoint from useState(portalElement) triggers re-render when ref sets it, so handleBlur stays current. onBlurCapture receives the latest handler.
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (mountPoint && relatedTarget && mountPoint.contains(relatedTarget)) {
        return;
      }
      onBlur?.(e);
    },
    [mountPoint, onBlur],
  );

  const handleChange = useCallback(
    (editorView: Parameters<typeof getEditorValue>[0]) => {
      onChange?.(getEditorValue(editorView) as ADFDocument);
    },
    [onChange],
  );

  return (
    <Box
      as="div"
      xcss={editorContainerStyles}
      onFocusCapture={onFocus}
      onBlurCapture={handleBlur}
      {...{ 'data-databases-editor': true }}
    >
      <style>
        {DATABASES_EDITOR_CSS}
        {LINK_PICKER_CSS}
      </style>
      <ComposableEditor
        appearance="chromeless"
        minHeight={0}
        preset={preset}
        mentionProvider={getMentionProvider(accountId)}
        emojiProvider={emojiProvider}
        linking={linkingOptions}
        onChange={handleChange}
        disabled={disabled}
        defaultValue={defaultValue}
        popupsMountPoint={mountPoint}
        shouldFocus={shouldFocus}
      />
      {!popupsMountPoint && <Box as="div" ref={setPortalElement} />}
    </Box>
  );
};

export const DatabasesEditor: React.FC<DatabasesEditorProps> = (props) => {
  return (
    <ErrorBoundary>
      <SmartCardProvider client={smartLinkClient}>
        <EditorContext>
          <DatabasesEditorInternal {...props} />
        </EditorContext>
      </SmartCardProvider>
    </ErrorBoundary>
  );
};
